import React, { useEffect, useState } from 'react';
import { 
  Typography, Card, Button, Select, Space, Table, Upload, 
  Modal, Form, Input, InputNumber, Tag, Divider, 
  Alert, Steps, Radio, message, Statistic, Row, Col, Popconfirm, Slider, Segmented
} from 'antd';
import { 
  UploadOutlined, PlusOutlined, EnvironmentOutlined, 
  MailOutlined, MessageOutlined,
  CheckCircleOutlined, StopOutlined, DownloadOutlined,
  EyeOutlined, LinkOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuth } from '../../../contexts/AuthContext';
import './ProximityMarketing.css';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;
const { Step } = Steps;

const ProximityMarketing = () => {
  const { token } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  
  // CRM Data
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);

  // Campaign Settings
  const [campaigns, setCampaigns] = useState([]);
  const [campaignModalVisible, setCampaignModalVisible] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form] = Form.useForm();
  const [deletingCustomerId, setDeletingCustomerId] = useState(null);
  const [, setEmailTemplates] = useState([]);
  const [, setLoadingEmailTemplates] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [emailPreviewVisible, setEmailPreviewVisible] = useState(false);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');

  // Geotab Integration
  const [geotabDbOptions, setGeotabDbOptions] = useState([]);
  const [selectedDatabases, setSelectedDatabases] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [trackingActive, setTrackingActive] = useState(false);

  const [viewCampaignModal, setViewCampaignModal] = useState(false);
  const [selectedViewCampaign, setSelectedViewCampaign] = useState(null);

  const authHeader = () => (token ? { Authorization: `Bearer ${token}` } : {});

  // Fetch Geotab databases
  const fetchGeotabAccounts = async () => {
    try {
      const { data } = await axios.get('/api/integrations/geotab/accounts', { headers: authHeader() });
      const items = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
      const opts = items
        .map(a => a?.database)
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .map(db => ({ value: db, label: db }));
      setGeotabDbOptions(opts);
      setSelectedDatabases(opts.map(o => o.value));
    } catch (e) {
      setGeotabDbOptions([]);
    }
  };

  const buildEmailHtml = (values) => {
    const logoUrl = values.email_logo_url || '';
    const heading = values.email_heading || '';
    const body = (values.email_body || '').replace(/\n/g, '<br/>');
    const ctaText = values.email_cta_text || '';
    const ctaUrl = values.email_cta_url || '';
    const footer = values.email_footer || 'You received this email because you are a valued customer.';
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:20px 0;"><tr><td align="center"><table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">${logoUrl ? `<tr><td style="background:#fff;padding:24px 40px;text-align:center;border-bottom:1px solid #f0f0f0;"><img src="${logoUrl}" alt="Logo" style="max-height:80px;max-width:220px;object-fit:contain;"/></td></tr>` : ''}<tr><td style="padding:40px 40px 24px;">${heading ? `<h1 style="margin:0 0 16px;font-size:24px;color:#1a1a1a;font-weight:700;">${heading}</h1>` : ''}<div style="font-size:15px;color:#444;line-height:1.7;">${body}</div>${ctaText && ctaUrl ? `<div style="text-align:center;margin:32px 0;"><a href="${ctaUrl}" style="background:#1890ff;color:#fff;padding:14px 32px;border-radius:6px;text-decoration:none;font-weight:600;font-size:15px;display:inline-block;">${ctaText}</a></div>` : ''}</td></tr><tr><td style="background:#f8f8f8;padding:20px 40px;border-top:1px solid #eee;text-align:center;"><p style="margin:0;font-size:12px;color:#999;">${footer}</p><p style="margin:8px 0 0;font-size:12px;color:#bbb;">To unsubscribe, reply STOP.</p></td></tr></table></td></tr></table></body></html>`;
  };

  const handleLogoUpload = async ({ file, onSuccess, onError }) => {
    setLogoUploading(true);
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const { data } = await axios.post('/api/crm/logo-upload', formData, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' }
      });
      form.setFieldsValue({ email_logo_url: data.url });
      onSuccess(data);
      message.success('Logo uploaded successfully');
    } catch (e) {
      onError(e);
      message.error('Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      if (!token) return;
      setLoadingEmailTemplates(true);
      const params = new URLSearchParams();
      params.set('category', 'email');
      const { data } = await axios.get(`/api/templates?${params.toString()}`, { headers: authHeader() });
      const items = Array.isArray(data) ? data : [];
      setEmailTemplates(items);
    } catch (e) {
      setEmailTemplates([]);
    } finally {
      setLoadingEmailTemplates(false);
    }
  };

  const exportSelectedCustomers = () => {
    if (selectedCustomerIds.length === 0) {
      message.warning('Select at least one customer to export');
      return;
    }

    const selectedRows = customers.filter(cust => selectedCustomerIds.includes(cust.id));
    if (selectedRows.length === 0) {
      message.warning('No matching customers to export');
      return;
    }

    const header = ['Name', 'Email', 'Phone', 'Address', 'LastContacted', 'OptedOut'];
    const rows = selectedRows.map(cust => [
      cust.name || '',
      cust.email || '',
      cust.phone || '',
      cust.address || '',
      cust.last_contacted ? dayjs(cust.last_contacted).format('YYYY-MM-DD') : '',
      cust.opted_out ? 'Yes' : 'No'
    ]);

    const csv = [header, ...rows]
      .map(cols => cols.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `customers_export_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    message.success(`Exported ${selectedRows.length} customer(s)`);
  };

  const deleteCustomerRow = async (customerId) => {
    try {
      setDeletingCustomerId(customerId);
      await axios.delete(`/api/crm/customers/${customerId}`, { headers: authHeader() });
      message.success('Customer deleted');
      fetchCustomers();
            setSelectedCustomerIds(prev => prev.filter(id => id !== customerId));
    } catch (e) {
      message.error(e?.response?.data?.error || 'Failed to delete customer');
    } finally {
      setDeletingCustomerId(null);
    }
  };

  const deleteSelectedCustomers = async () => {
    if (selectedCustomerIds.length === 0) {
      message.warning('Select at least one customer to delete');
      return;
    }
    try {
      setLoadingCustomers(true);
      await Promise.all(
        selectedCustomerIds.map(id =>
          axios.delete(`/api/crm/customers/${id}`, { headers: authHeader() })
        )
      );
      message.success(`Deleted ${selectedCustomerIds.length} customer(s)`);
      setSelectedCustomerIds([]);
      fetchCustomers();
    } catch (e) {
      message.error(e?.response?.data?.error || 'Failed to delete selected customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const deleteCustomers = async () => {
    try {
      setLoadingCustomers(true);
      await axios.delete('/api/crm/customers', { headers: authHeader() });
      message.success('Customer database deleted');
      fetchCustomers();
          } catch (e) {
      message.error(e?.response?.data?.error || 'Failed to delete customer database');
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Fetch vehicles
  const fetchDevices = async () => {
    if (!selectedDatabases.length) return;
    try {
      const query = new URLSearchParams();
      query.set('databases', selectedDatabases.join(','));
      const { data } = await axios.get(`/api/integrations/geotab/devices?${query.toString()}`, { headers: authHeader() });
      const items = Array.isArray(data?.devices) ? data.devices : [];
      setDevices(items);
    } catch (e) {
      message.error('Failed to load vehicles');
    }
  };

  // Fetch customers from backend
  const fetchCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const { data } = await axios.get('/api/crm/customers', { headers: authHeader() });
      const items = Array.isArray(data?.customers) ? data.customers : [];
      setCustomers(items);
      setSelectedCustomerIds([]);
          } catch (e) {
      message.error('Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Fetch campaigns
  const fetchCampaigns = async () => {
    try {
      const { data } = await axios.get('/api/crm/campaigns', { headers: authHeader() });
      const items = Array.isArray(data?.campaigns) ? data.campaigns : [];
      setCampaigns(items);
          } catch (e) {
      message.error('Failed to load campaigns');
    }
  };

  useEffect(() => {
    fetchGeotabAccounts();
    fetchCustomers();
    fetchCampaigns();
    fetchEmailTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedDatabases.length > 0) {
      fetchDevices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDatabases]);

  // Upload CRM CSV
  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', fileList[0]);

    try {
      const { data } = await axios.post('/api/crm/customers/upload', formData, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' }
      });
      message.success(`Uploaded ${data.count} customers`);
      setUploadModalVisible(false);
      setFileList([]);
      fetchCustomers();
    } catch (e) {
      message.error(e?.response?.data?.error || 'Failed to upload CRM data');
    }
  };

  // Save campaign
  const saveCampaign = async (values) => {
    try {
      const geofenceFeet = values.geofence_radius_feet ?? 500;
      const geofenceMeters = Math.round(Number(geofenceFeet) * 0.3048);

      const triggerMode = values.trigger_mode || 'moving';
      const stopDuration = triggerMode === 'stopped' ? values.stop_duration_minutes ?? 5 : null;

      const builtEmailBody = (values.message_type === 'email' || values.message_type === 'both')
        ? buildEmailHtml(values)
        : values.email_body;

      const payload = {
        name: values.name,
        message_type: values.message_type,
        email_subject: values.email_subject,
        email_body: builtEmailBody,
        sms_message: values.sms_message,
        geofence_radius_meters: geofenceMeters,
        geofence_radius_feet: geofenceFeet,
        frequency_days: values.frequency_days,
        trigger_mode: triggerMode,
        stop_duration_minutes: stopDuration,
        devices: selectedDevices,
        status: values.status || 'active'
      };

      if (editingCampaign) {
        await axios.put(`/api/crm/campaigns/${editingCampaign.id}`, payload, { headers: authHeader() });
        message.success('Campaign updated');
      } else {
        await axios.post('/api/crm/campaigns', payload, { headers: authHeader() });
        message.success('Campaign created');
      }

      setCampaignModalVisible(false);
      setEditingCampaign(null);
      form.resetFields();
      fetchCampaigns();
    } catch (e) {
      message.error(e?.response?.data?.error || 'Failed to save campaign');
    }
  };

  // Delete campaign
  const deleteCampaign = async (id) => {
    try {
      await axios.delete(`/api/crm/campaigns/${id}`, { headers: authHeader() });
      message.success('Campaign deleted');
      fetchCampaigns();
    } catch (e) {
      message.error('Failed to delete campaign');
    }
  };

  // Toggle campaign status
  const toggleCampaignStatus = async (campaign) => {
    try {
      const newStatus = campaign.status === 'active' ? 'paused' : 'active';
      await axios.put(`/api/crm/campaigns/${campaign.id}`, { status: newStatus }, { headers: authHeader() });
      message.success(`Campaign ${newStatus}`);
      fetchCampaigns();
    } catch (e) {
      message.error('Failed to update campaign');
    }
  };

  // Start/Stop live tracking
  const toggleTracking = async () => {
    if (!trackingActive && selectedDevices.length === 0) {
      message.warning('Please select at least one vehicle to track');
      return;
    }

    try {
      if (trackingActive) {
        await axios.post('/api/crm/tracking/stop', {}, { headers: authHeader() });
        setTrackingActive(false);
        message.success('Live tracking stopped');
      } else {
        await axios.post('/api/crm/tracking/start', { devices: selectedDevices }, { headers: authHeader() });
        setTrackingActive(true);
        message.success('Live tracking started');
      }
          } catch (e) {
      message.error(e?.response?.data?.error || 'Failed to toggle tracking');
    }
  };

  const customerColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Address', dataIndex: 'address', key: 'address', ellipsis: true },
    { 
      title: 'Last Contacted', 
      dataIndex: 'last_contacted', 
      key: 'last_contacted',
      render: (val) => val ? dayjs(val).format('M/D/YYYY') : 'Never'
    },
    {
      title: 'Status',
      dataIndex: 'opted_out',
      key: 'opted_out',
      render: (val) => val ? <Tag color="red">Opted Out</Tag> : <Tag color="green">Active</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Delete customer?"
          description={`Remove ${record.name || 'this customer'} from the database?`}
          okText="Delete"
          okButtonProps={{ danger: true, loading: deletingCustomerId === record.id }}
          onConfirm={() => deleteCustomerRow(record.id)}
        >
          <Button size="small" danger loading={deletingCustomerId === record.id}>Delete</Button>
        </Popconfirm>
      )
    }
  ];

  const customerRowSelection = {
    selectedRowKeys: selectedCustomerIds,
    onChange: (selectedRowKeys) => {
      setSelectedCustomerIds(selectedRowKeys);
    },
  };

  const campaignColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { 
      title: 'Type', 
      dataIndex: 'message_type', 
      key: 'message_type',
      render: (val) => {
        if (val === 'email') return <Tag icon={<MailOutlined />} color="blue">Email</Tag>;
        if (val === 'sms') return <Tag icon={<MessageOutlined />} color="green">SMS</Tag>;
        return <Tag color="purple">Both</Tag>;
      }
    },
    {
      title: 'Trigger',
      dataIndex: 'trigger_mode',
      key: 'trigger_mode',
      render: (val, record) => {
        const mode = (val || 'moving');
        const label = mode === 'stopped' ? 'When stopped' : mode === 'both' ? 'Moving + stopped' : 'When moving';
        const stopInfo = record.stop_duration_minutes && (mode === 'stopped' || mode === 'both')
          ? ` (${record.stop_duration_minutes} min)`
          : '';
        return <Tag color="geekblue">{`${label}${stopInfo}`}</Tag>;
      }
    },
    { 
      title: 'Trigger Distance', 
      dataIndex: 'geofence_radius_meters', 
      key: 'geofence_radius_meters',
      render: (val) => `${Math.round(val / 0.3048)} ft`
    },
    { 
      title: 'Frequency', 
      dataIndex: 'frequency_days', 
      key: 'frequency_days',
      render: (val) => `Every ${val} days`
    },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status',
      render: (val) => val === 'active' ? <Tag color="green">Active</Tag> : <Tag color="orange">Paused</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => { 
            setEditingCampaign(record);
            form.setFieldsValue({
              ...record,
              geofence_radius_feet: Math.round(Number(record.geofence_radius_meters || 152) / 0.3048),
              trigger_mode: record.trigger_mode || 'moving',
              stop_duration_minutes: record.stop_duration_minutes || 5
            });
            setCampaignModalVisible(true);
          }
          }>
            Edit
          </Button>
          <Button 
            size="small" 
            type={record.status === 'active' ? 'default' : 'primary'}
            onClick={() => toggleCampaignStatus(record)}
          >
            {record.status === 'active' ? 'Pause' : 'Activate'}
          </Button>
          <Button size="small" danger onClick={() => deleteCampaign(record.id)}>Delete</Button>
        </Space>
      )
    }
  ];

  const steps = [
    {
      title: 'Import CRM Data',
      icon: <UploadOutlined />,
      description: 'Upload customer list'
    },
    {
      title: 'Configure Campaign',
      icon: <MessageOutlined />,
      description: 'Set up messages'
    },
    {
      title: 'Live Tracking',
      icon: <EnvironmentOutlined />,
      description: 'Track vehicles'
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={3}>Proximity Marketing CRM</Title>
      <Paragraph type="secondary">
        Engage customers automatically when your vehicles are in their neighborhood
      </Paragraph>

      <Alert
        message="Live Proximity Marketing"
        description="Track your fleet in real-time and automatically notify customers when a vehicle is nearby. Messages are limited to your configured frequency to prevent spam."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        {steps.map((item, index) => (
          <Step key={index} title={item.title} description={item.description} icon={item.icon} />
        ))}
      </Steps>

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* Step 0: Import CRM Data */}
        {currentStep === 0 && (
          <Card 
            title="Customer Database" 
            extra={
              <Space>
                <Button icon={<UploadOutlined />} onClick={() => setUploadModalVisible(true)}>
                  Import CRM Data
                </Button>
                <Popconfirm
                  title="Delete customer database?"
                  description="This will remove all customer records, message logs, and tracking sessions."
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                  onConfirm={deleteCustomers}
                  disabled={customers.length === 0 && !loadingCustomers}
                >
                  <Button danger loading={loadingCustomers}>
                    Delete Database
                  </Button>
                </Popconfirm>
              </Space>
            }
          >
            <Table 
              columns={customerColumns} 
              dataSource={customers} 
              rowKey="id" 
              loading={loadingCustomers}
              rowSelection={customerRowSelection}
              pagination={{ pageSize: 10 }}
            />
            <Divider />
            <Space style={{ marginBottom: 16 }}>
              <Popconfirm
                title="Delete selected customers?"
                description={`Remove ${selectedCustomerIds.length} selected customer(s)?`}
                okText="Delete"
                okButtonProps={{ danger: true, loading: loadingCustomers }}
                onConfirm={deleteSelectedCustomers}
                disabled={selectedCustomerIds.length === 0}
              >
                <Button danger disabled={selectedCustomerIds.length === 0} loading={loadingCustomers}>
                  Delete Selected
                </Button>
              </Popconfirm>
              <Button
                icon={<DownloadOutlined />}
                disabled={selectedCustomerIds.length === 0}
                onClick={exportSelectedCustomers}
              >
                Export Selected
              </Button>
            </Space>
            <Button type="primary" onClick={() => setCurrentStep(1)} disabled={customers.length === 0}>
              Next: Configure Campaign
            </Button>
          </Card>
        )}

        {/* Step 1: Configure Campaign */}
        {currentStep === 1 && (
          <Card 
            title="Marketing Campaigns" 
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingCampaign(null);
                  form.resetFields();
                  setCampaignModalVisible(true);
                }}
              >
                Create Campaign
              </Button>
            }
          >
            <Table 
              columns={campaignColumns} 
              dataSource={campaigns} 
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
            <Divider />
            <Space>
              <Button onClick={() => setCurrentStep(0)}>Previous</Button>
              <Button type="primary" onClick={() => setCurrentStep(2)} disabled={campaigns.length === 0}>
                Next: Start Tracking
              </Button>
            </Space>
          </Card>
        )}

        {/* Step 2: Live Tracking */}
        {currentStep === 2 && (
          <Card title="Live Vehicle Tracking">
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div>
                <Text strong>Select Geotab Databases</Text>
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '100%', marginTop: 6 }}
                  value={selectedDatabases}
                  onChange={setSelectedDatabases}
                  options={geotabDbOptions}
                  placeholder="Select databases"
                />
              </div>

              {devices.length > 0 && (
                <div>
                  <Text strong>Select Vehicles to Track</Text>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%', marginTop: 6 }}
                    value={selectedDevices}
                    onChange={setSelectedDevices}
                    options={devices.map(d => ({ value: d.id, label: d.name || d.serialNumber }))}
                    placeholder="Select vehicles"
                  />
                </div>
              )}

              <Alert
                message={trackingActive ? "Live tracking is active" : "Tracking is stopped"}
                description={trackingActive 
                  ? "Your vehicles are being monitored. Customers will receive notifications when vehicles enter their geofence area (respecting frequency limits)."
                  : "Click 'Start Tracking' to begin monitoring vehicles and triggering proximity campaigns."
                }
                type={trackingActive ? "success" : "warning"}
                showIcon
              />

              <Button 
                type="primary" 
                size="large" 
                danger={trackingActive}
                onClick={toggleTracking}
                icon={trackingActive ? <StopOutlined /> : <CheckCircleOutlined />}
              >
                {trackingActive ? 'Stop Live Tracking' : 'Start Live Tracking'}
              </Button>

              <Divider />
              <Button onClick={() => setCurrentStep(1)}>Previous</Button>
            </Space>
          </Card>
        )}
      </Space>

      {/* Email Preview Modal */}
      <Modal
        title="Email Preview"
        open={emailPreviewVisible}
        onCancel={() => setEmailPreviewVisible(false)}
        footer={<Button onClick={() => setEmailPreviewVisible(false)}>Close</Button>}
        width={680}
      >
        <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden', background: '#f4f4f4' }}>
          <iframe
            srcDoc={emailPreviewHtml}
            title="Email Preview"
            style={{ width: '100%', height: 500, border: 'none', display: 'block' }}
            sandbox="allow-same-origin"
          />
        </div>
      </Modal>

      {/* Upload Modal */}
      <Modal
        title="Upload CRM Data"
        open={uploadModalVisible}
        onOk={handleUpload}
        onCancel={() => { setUploadModalVisible(false); setFileList([]); }}
        okText="Upload"
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            message="CSV Format Required"
            description="Your CSV should include columns: Name, Email, Phone, Address (full address including city, state, zip)"
            type="info"
            showIcon
          />
          <Upload
            beforeUpload={(file) => {
              setFileList([file]);
              return false;
            }}
            onRemove={() => setFileList([])}
            fileList={fileList}
            accept=".csv"
          >
            <Button icon={<UploadOutlined />}>Select CSV File</Button>
          </Upload>
        </Space>
      </Modal>

      {/* Campaign Modal */}
      <Modal
        title={editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
        open={campaignModalVisible}
        onOk={() => form.submit()}
        onCancel={() => { setCampaignModalVisible(false); setEditingCampaign(null); form.resetFields(); }}
        width={650}
        okText="Save"
      >
        <Form form={form} layout="vertical" onFinish={saveCampaign} initialValues={{ message_type: 'both', geofence_radius_feet: 500, frequency_days: 90, trigger_mode: 'moving', stop_duration_minutes: 5 }}>
          <Form.Item name="name" label="Campaign Name" rules={[{ required: true }]}> 
            <Input placeholder="e.g., Neighborhood Service Alert" className="pm-placeholder-muted" />
          </Form.Item>

          <Form.Item name="message_type" label="Message Type">
            <Radio.Group>
              <Radio value="email">Email Only</Radio>
              <Radio value="sms">SMS Only</Radio>
              <Radio value="both">Both</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item shouldUpdate={(prev, curr) =>
            prev.message_type !== curr.message_type ||
            prev.email_logo_url !== curr.email_logo_url
          } noStyle>
            {({ getFieldValue, setFieldsValue }) => {
              const type = getFieldValue('message_type') || 'both';
              const logoUrl = getFieldValue('email_logo_url');

              return (
                <>
                  {(type === 'email' || type === 'both') && (
                    <>
                      {/* Logo */}
                      <Form.Item label="Company Logo">
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                          <Space align="center">
                            <Form.Item name="email_logo_url" noStyle>
                              <Input
                                placeholder="https://yoursite.com/logo.png or upload below"
                                prefix={<LinkOutlined />}
                                style={{ width: 320 }}
                                allowClear
                              />
                            </Form.Item>
                            <Upload accept="image/*" showUploadList={false} customRequest={handleLogoUpload}>
                              <Button icon={<UploadOutlined />} loading={logoUploading}>Upload</Button>
                            </Upload>
                          </Space>
                          {logoUrl && (
                            <div style={{ padding: '8px 12px', background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 6, display: 'inline-block' }}>
                              <img src={logoUrl} alt="Logo preview" style={{ maxHeight: 56, maxWidth: 180, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                            </div>
                          )}
                        </Space>
                      </Form.Item>

                      {/* Subject */}
                      <Form.Item name="email_subject" label="Email Subject" rules={[{ required: type !== 'sms', message: 'Subject is required' }]}>
                        <Input placeholder="We're in your neighborhood!" className="pm-placeholder-muted" />
                      </Form.Item>

                      {/* Heading */}
                      <Form.Item name="email_heading" label="Email Heading" extra="Displayed as a bold title above your message">
                        <Input placeholder="Hi {name}, we're nearby!" className="pm-placeholder-muted" />
                      </Form.Item>

                      {/* Body */}
                      <Form.Item name="email_body" label="Message Body" rules={[{ required: type !== 'sms', message: 'Message is required' }]}>
                        <TextArea
                          rows={5}
                          placeholder={`Hi {name},\n\nOne of our trucks is in your area today. Reply or click the button below to schedule your service!`}
                          className="pm-placeholder-muted"
                        />
                      </Form.Item>

                      {/* CTA Button */}
                      <Row gutter={12}>
                        <Col span={12}>
                          <Form.Item name="email_cta_text" label="Button Text">
                            <Input placeholder="Book Now" className="pm-placeholder-muted" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="email_cta_url" label="Button URL">
                            <Input placeholder="https://yoursite.com/book" prefix={<LinkOutlined />} className="pm-placeholder-muted" />
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* Footer */}
                      <Form.Item name="email_footer" label="Footer Text">
                        <Input placeholder="You received this email because you are a valued customer." className="pm-placeholder-muted" />
                      </Form.Item>

                      {/* Preview */}
                      <Form.Item>
                        <Button
                          icon={<EyeOutlined />}
                          onClick={() => {
                            const vals = form.getFieldsValue();
                            setEmailPreviewHtml(buildEmailHtml(vals));
                            setEmailPreviewVisible(true);
                          }}
                        >
                          Preview Email
                        </Button>
                      </Form.Item>
                    </>
                  )}

                  {(type === 'sms' || type === 'both') && (
                    <Form.Item name="sms_message" label="SMS Message">
                      <TextArea rows={3} placeholder="Hi {name}! We're nearby. Book your free pest inspection: [link]" maxLength={160} showCount className="pm-placeholder-muted" />
                    </Form.Item>
                  )}
                </>
              );
            }}
          </Form.Item>

          <Form.Item
            name="geofence_radius_feet"
            label="Trigger Distance (feet)"
            tooltip={{ title: <span style={{ color: '#000' }}>When a vehicle enters within this distance of a customer, they'll receive your message</span> }}
            extra={<span style={{ color: '#000' }}>When a vehicle enters within this distance of a customer, they'll receive your message</span>}
          >
            <Slider
              min={100}
              max={5280}
              step={50}
              marks={{ 100: '100 ft', 500: '500 ft', 1000: '1,000 ft', 2640: '½ mi', 5280: '1 mi' }}
              tooltip={{ formatter: (value) => <span style={{ color: '#000' }}>{`${value.toLocaleString()} ft`}</span> }}
            />
          </Form.Item>

          <Form.Item name="trigger_mode" label="Trigger Condition" tooltip={{ title: <span style={{ color: '#000' }}>Choose when messages should be sent</span> }}>
            <Segmented
              options={[
                { label: 'When Vehicle is Moving', value: 'moving' },
                { label: 'When Vehicle Stops', value: 'stopped' },
                { label: 'Both', value: 'both' }
              ]}
            />
          </Form.Item>

          <Form.Item shouldUpdate={(prev, curr) => prev.trigger_mode !== curr.trigger_mode} noStyle>
            {({ getFieldValue, setFieldsValue }) => {
              const mode = getFieldValue('trigger_mode');
              if (mode !== 'stopped' && mode !== 'both') {
                if (getFieldValue('stop_duration_minutes') !== undefined) {
                  setFieldsValue({ stop_duration_minutes: undefined });
                }
                return null;
              }
              return (
                <Form.Item
                  name="stop_duration_minutes"
                  label="Stop Duration Threshold (minutes)"
                  tooltip={{ title: <span style={{ color: '#000' }}>Send messages only after the vehicle has stopped for this many minutes</span> }}
                  extra={<span style={{ color: '#000' }}>Send messages only after the vehicle has stopped for this many minutes</span>}
                >
                  <Slider
                    min={1}
                    max={60}
                    step={1}
                    marks={{ 1: '1', 5: '5', 15: '15', 30: '30', 60: '60' }}
                    tooltip={{ formatter: (value) => <span style={{ color: '#000' }}>{`${value} minute${value === 1 ? '' : 's'}`}</span> }}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            name="frequency_days"
            label="Contact Frequency (days)"
            tooltip={{ title: <span style={{ color: '#000' }}>Minimum days between messages to same customers</span> }}
            extra={<span style={{ color: '#000' }}>Minimum days between messages to same customers</span>}
          >
            <InputNumber min={1} max={365} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Campaign View Modal */}
      <Modal
        title="Campaign Details"
        open={viewCampaignModal}
        onCancel={() => {
          setViewCampaignModal(false);
          setSelectedViewCampaign(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setViewCampaignModal(false);
            setSelectedViewCampaign(null);
          }}>
            Close
          </Button>
        ]}
        width={700}
      >
        {selectedViewCampaign && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {/* Campaign Status Overview */}
            <Card size="small" title="Status Overview">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic
                    title="Campaign Status"
                    value={selectedViewCampaign.status}
                    valueStyle={{ 
                      color: selectedViewCampaign.status === 'active' ? '#52c41a' : '#faad14',
                      textTransform: 'capitalize'
                    }}
                    prefix={selectedViewCampaign.status === 'active' ? <CheckCircleOutlined /> : <StopOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Tracking Status"
                    value={
                      selectedViewCampaign.is_tracking_active ? 'Active' :
                      selectedViewCampaign.tracking_stopped ? 'Stopped' : 'Not Started'
                    }
                    valueStyle={{ 
                      color: selectedViewCampaign.is_tracking_active ? '#52c41a' : '#ff4d4f'
                    }}
                    prefix={selectedViewCampaign.is_tracking_active ? <CheckCircleOutlined /> : <StopOutlined />}
                  />
                </Col>
              </Row>
            </Card>

            {/* Campaign Statistics */}
            <Card size="small" title="Campaign Statistics">
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic
                    title="Total Customers"
                    value={selectedViewCampaign.total_customers}
                    prefix={<EnvironmentOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Contacted"
                    value={selectedViewCampaign.contacted_customers}
                    prefix={<MailOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Messages (30d)"
                    value={selectedViewCampaign.messages_sent_30d}
                    prefix={<MessageOutlined />}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
              </Row>
            </Card>

            {/* Campaign Configuration */}
            <Card size="small" title="Configuration">
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <div>
                  <Text strong>Message Type: </Text>
                  {selectedViewCampaign.message_type === 'email' && <Tag icon={<MailOutlined />} color="blue">Email</Tag>}
                  {selectedViewCampaign.message_type === 'sms' && <Tag icon={<MessageOutlined />} color="green">SMS</Tag>}
                  {selectedViewCampaign.message_type === 'both' && <Tag color="purple">Both</Tag>}
                </div>
                <div>
                  <Text strong>Geofence Radius: </Text>
                  <Text>{selectedViewCampaign.geofence_radius_meters}m ({(selectedViewCampaign.geofence_radius_meters / 1609.34).toFixed(2)} miles)</Text>
                </div>
                <div>
                  <Text strong>Contact Frequency: </Text>
                  <Text>Every {selectedViewCampaign.frequency_days} days</Text>
                </div>
                <div>
                  <Text strong>Trigger Mode: </Text>
                  <Tag color="geekblue">
                    {selectedViewCampaign.trigger_mode === 'stopped' ? 'When Stopped' : 
                     selectedViewCampaign.trigger_mode === 'both' ? 'Moving + Stopped' : 'When Moving'}
                    {selectedViewCampaign.stop_duration_minutes && 
                     (selectedViewCampaign.trigger_mode === 'stopped' || selectedViewCampaign.trigger_mode === 'both') &&
                     ` (${selectedViewCampaign.stop_duration_minutes} min)`}
                  </Tag>
                </div>
                <div>
                  <Text strong>Tracked Vehicles: </Text>
                  <Text>{Array.isArray(selectedViewCampaign.devices) ? selectedViewCampaign.devices.length : 0}</Text>
                </div>
              </Space>
            </Card>

            {/* Message Content */}
            {(selectedViewCampaign.message_type === 'email' || selectedViewCampaign.message_type === 'both') && (
              <Card size="small" title="Email Content">
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <div>
                    <Text strong>Subject: </Text>
                    <Text>{selectedViewCampaign.email_subject || 'N/A'}</Text>
                  </div>
                  <div>
                    <Text strong>Body: </Text>
                    <div style={{ 
                      marginTop: 8, 
                      padding: 12, 
                      background: '#f5f5f5', 
                      borderRadius: 4,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {selectedViewCampaign.email_body || 'N/A'}
                    </div>
                  </div>
                </Space>
              </Card>
            )}

            {(selectedViewCampaign.message_type === 'sms' || selectedViewCampaign.message_type === 'both') && (
              <Card size="small" title="SMS Content">
                <div style={{ 
                  padding: 12, 
                  background: '#f5f5f5', 
                  borderRadius: 4,
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedViewCampaign.sms_message || 'N/A'}
                </div>
              </Card>
            )}

            
            {/* Timestamps */}
            <Card size="small" title="Campaign Timestamps">
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <div>
                  <Text strong>Created: </Text>
                  <Text>{dayjs(selectedViewCampaign.created_at).format('MMM D, YYYY h:mm A')}</Text>
                </div>
                <div>
                  <Text strong>Last Updated: </Text>
                  <Text>{dayjs(selectedViewCampaign.updated_at).format('MMM D, YYYY h:mm A')}</Text>
                </div>
              </Space>
            </Card>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default ProximityMarketing;
